<template>
  <div class="ai-recommendation-panel">
    <div class="section-header">
      <h4 class="section-title">3 切换备选专业</h4>
      <el-button 
        type="primary" 
        size="mini" 
        @click="getAIRecommendations"
        :loading="aiLoading"
        class="ai-recommend-btn"
      >
        <i class="el-icon-magic-stick"></i> AI 智能推荐
      </el-button>
    </div>
    
    <div class="majors-list">
      <!-- 动态专业列表 -->
      <div 
        v-for="major in displayMajors" 
        :key="major.id"
        class="major-item" 
        :class="{ 'active': selectedMajor === major.id }"
        @click="selectMajor(major.id)"
      >
        <div class="major-header">
          <el-radio v-model="selectedMajor" :label="major.id">
            {{ major.name }}
          </el-radio>
          <i 
            class="el-icon-arrow-down expand-icon" 
            :class="{ 'expanded': expandedMajor === major.id }"
            @click.stop="toggleMajorDetails(major.id)"
          ></i>
        </div>
        <transition name="slide-down">
          <div v-show="expandedMajor === major.id" class="major-details">
            <div v-if="major.details">
              <!-- AI推荐的专业显示详细分析 -->
              <p class="detail-item detail-full">
                {{ formatMajorDetailsPreview(major.details) }}
              </p>
            </div>
            <div v-else>
              <!-- 默认专业显示简要信息 -->
              <p class="detail-item">
                <i class="el-icon-info"></i> {{ major.description || '暂无详细描述' }}
              </p>
            </div>
          </div>
        </transition>
      </div>
    </div>
    
  </div>
</template>

<script>
import { 
  getMajorRecommendations, 
  createRecommendationLoadingModal, 
  removeRecommendationLoadingModal,
  updateLoadingProgress,
  cancelCurrentRecommendation
} from '@/utils/majorRecommendation'

export default {
  name: 'AIRecommendationPanel',
  props: {
    formData: {
      type: Object,
      required: true
    },
    selectedMajor: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      aiLoading: false,
      aiRecommendations: [],
      expandedMajor: null,
      // 默认的备选专业
      defaultMajors: [
        {
          id: 1,
          name: '电子信息工程',
          sub_id: null, // 实际的 sub_id 需要从接口获取
          description: '核心课程:电路原理、人工智能基础 | 就业方向:珠三角电子企业工程师 | 平均薪资(广东):8k/月'
        },
        {
          id: 2,
          name: '汉语言文学(师范)',
          sub_id: null,
          description: '核心课程:教育学、语文教学法 | 就业方向:广东公立学校语文老师 | 稳定性:就业率95%'
        },
        {
          id: 3,
          name: '自动化',
          sub_id: null,
          description: '核心课程:自动控制原理、机器人技术 | 就业方向:新能源企业控制工程师 | 风险提示:行业需求波动中等'
        }
      ],
      // 当前显示的专业列表（默认或AI推荐）
      displayMajors: []
    }
  },
  created() {
    // 初始化显示默认专业
    this.displayMajors = [...this.defaultMajors]
    // 立即通知父组件默认专业列表
    this.$emit('ai-majors-update', this.displayMajors)
  },
  methods: {
    selectMajor(majorId) {
      this.$emit('major-select', majorId)
      // 同时触发专业详情显示
      const major = this.displayMajors.find(m => m.id === majorId)
      if (major) {
        this.$emit('major-detail-select', major)
      }
    },
    
    toggleMajorDetails(majorId) {
      this.expandedMajor = this.expandedMajor === majorId ? null : majorId
    },
    
    // AI推荐相关方法
    async getAIRecommendations() {
      // 验证用户画像是否填写完整
      if (!this.validateFormData()) {
        this.$message.warning('请先完整填写用户画像信息（至少填写分数段、优势学科、职业兴趣和目标就业地）')
        return
      }
      
      this.aiLoading = true
      createRecommendationLoadingModal()
      
      try {
        // 使用异步处理，传入进度回调
        const result = await getMajorRecommendations(this.formData, (status, progress) => {
          updateLoadingProgress(status, progress)
        })
        
        if (result.success && result.majors && result.majors.length > 0) {
          this.aiRecommendations = result.majors
          
          // 将AI推荐的专业转换为雷达图所需的格式
          const majorsForRadar = this.convertAIMajorsToRadarFormat(result.majors)
          
          // 更新显示的专业列表
          this.displayMajors = majorsForRadar
          
          // 传递给父组件更新雷达图
          this.$emit('ai-majors-update', majorsForRadar)
          
          // 自动选中第一个推荐专业
          this.$emit('major-select', 1)
          
          this.$message.success(`AI 成功为您推荐了 ${result.majors.length} 个专业！备选专业已更新`)
        } else if (result.rawOutput) {
          // 如果无法解析标准格式，在控制台输出原始结果
          console.log('AI 推荐原始结果:', result.rawOutput)
          this.$message.warning('AI 推荐结果格式异常，请查看控制台或稍后重试')
        } else {
          this.$message.warning('AI 推荐暂时无结果，请稍后重试')
        }
      } catch (error) {
        console.error('获取AI推荐失败:', error)
        
        if (error.message.includes('用户取消')) {
          this.$message.info('已取消AI推荐')
        } else if (error.message.includes('超时')) {
          this.$message.warning('AI推荐超时，请检查网络连接后重试')
        } else {
          this.$message.error(`获取推荐失败: ${error.message}`)
        }
      } finally {
        this.aiLoading = false
        removeRecommendationLoadingModal()
      }
    },
    
    convertAIMajorsToRadarFormat(aiMajors) {
      // 将AI推荐的专业转换为雷达图格式
      return aiMajors.map((major, index) => {
        // 从匹配度分数计算雷达图各维度的值
        const baseScore = parseFloat(major.score) * 10 // 将10分制转换为100分制
        
        // 根据详情内容智能解析各维度分数（如果无法解析则使用基础分数加随机偏移）
        const scores = this.extractScoresFromDetails(major.details, baseScore)
        
        return {
          id: index + 1,
          name: major.name,
          rank: major.rank,
          score: major.score,
          details: major.details,
          radarData: {
            studyAdaptability: scores.studyAdaptability,
            interestFit: scores.interestFit,
            resourceMatch: scores.resourceMatch,
            regionFit: scores.regionFit,
            riskFit: scores.riskFit
          }
        }
      })
    },
    
    extractScoresFromDetails(details, baseScore) {
      // 尝试从详情文本中提取各维度的分数
      // 这里使用简化的逻辑：基于基础分数生成合理的维度分数
      
      const variation = 15 // 允许的分数波动范围
      
      // 生成一个在 baseScore ± variation 范围内的随机分数
      const randomScore = (offset = 0) => {
        const score = baseScore + offset + (Math.random() * variation * 2 - variation)
        return Math.max(30, Math.min(100, Math.round(score)))
      }
      
      // 尝试从文本中提取关键词来调整分数
      let studyBonus = 0
      let interestBonus = 0
      let resourceBonus = 0
      let regionBonus = 0
      let riskBonus = 0
      
      if (details) {
        const lowerDetails = details.toLowerCase()
        
        // 学习适配度关键词
        if (lowerDetails.includes('学习') || lowerDetails.includes('课程') || lowerDetails.includes('难度')) {
          studyBonus = 5
        }
        
        // 兴趣契合度关键词
        if (lowerDetails.includes('兴趣') || lowerDetails.includes('研究型') || lowerDetails.includes('契合')) {
          interestBonus = 5
        }
        
        // 资源匹配度关键词
        if (lowerDetails.includes('资源') || lowerDetails.includes('家庭') || lowerDetails.includes('经济')) {
          resourceBonus = 5
        }
        
        // 地域适配度关键词
        if (lowerDetails.includes('北京') || lowerDetails.includes('上海') || lowerDetails.includes('就业地')) {
          regionBonus = 5
        }
        
        // 风险适配度关键词
        if (lowerDetails.includes('风险') || lowerDetails.includes('回报') || lowerDetails.includes('稳定')) {
          riskBonus = 5
        }
      }
      
      return {
        studyAdaptability: randomScore(studyBonus),
        interestFit: randomScore(interestBonus),
        resourceMatch: randomScore(resourceBonus),
        regionFit: randomScore(regionBonus),
        riskFit: randomScore(riskBonus)
      }
    },
    
    validateFormData() {
      return this.formData.scoreRange && 
             this.formData.subjects.length > 0 && 
             this.formData.careerType && 
             this.formData.targetRegions.length > 0
    },
    
    formatMajorDetailsPreview(details) {
      // 为展开的专业详情提供预览（前300个字符）
      if (!details) return ''
      const text = details.replace(/\n/g, ' ').trim()
      return text.length > 300 ? text.substring(0, 300) + '...' : text
    },
    
    
    // 重置推荐结果
    resetRecommendations() {
      this.aiRecommendations = []
      this.displayMajors = [...this.defaultMajors]
      this.$emit('ai-majors-update', this.displayMajors)
    },
    
    // 获取当前显示的专业列表
    getDisplayMajors() {
      return this.displayMajors
    },
    
    // 保存和加载进度
    saveProgress() {
      const data = {
        aiRecommendations: this.aiRecommendations,
        displayMajors: this.displayMajors
      }
      return data
    },
    
    loadProgress(data) {
      if (data) {
        this.aiRecommendations = data.aiRecommendations || []
        
        // 如果有保存的AI推荐专业，恢复显示
        if (data.displayMajors && data.displayMajors.length > 0) {
          this.displayMajors = data.displayMajors
          // 同时通知父组件更新专业列表
          this.$emit('ai-majors-update', data.displayMajors)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-recommendation-panel {
  .section-title {
    font-size: 15px;
    font-weight: 700;
    color: #000;
    margin-bottom: 20px;
    padding-left: 0;
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  
  .section-title {
    margin-bottom: 0;
  }
}

.ai-recommend-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  font-size: 12px;
  padding: 7px 12px;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
  }
  
  i {
    margin-right: 4px;
  }
}

.majors-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.major-item {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
  }
  
  &.active {
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.05);
  }
}

.major-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ai-tag {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 10px;
  font-weight: 600;
  border-radius: 3px;
  vertical-align: middle;
}

.expand-icon {
  font-size: 14px;
  color: #94a3b8;
  transition: transform 0.3s;
  
  &.expanded {
    transform: rotate(180deg);
  }
  
  &:hover {
    color: #3b82f6;
  }
}

.major-details {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.detail-item {
  font-size: 12px;
  color: #475569;
  margin: 8px 0;
  display: flex;
  align-items: flex-start;
  
  i {
    margin-right: 6px;
    margin-top: 2px;
    color: #64748b;
    font-size: 14px;
  }
  
  &.detail-full {
    line-height: 1.8;
    color: #475569;
    white-space: pre-wrap;
  }
}

.view-full-btn {
  padding: 0;
  color: #667eea;
  font-size: 12px;
  margin-top: 8px;
  
  &:hover {
    color: #764ba2;
  }
  
  i {
    margin-left: 4px;
    transition: transform 0.3s ease;
  }
  
  &:hover i {
    transform: translateX(3px);
  }
}

// AI详情弹窗样式
::v-deep .ai-detail-dialog {
  .el-dialog__header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 20px;
    
    .el-dialog__title {
      color: white;
      font-weight: 600;
    }
    
    .el-dialog__close {
      color: white;
    }
  }
  
  .el-dialog__body {
    padding: 20px;
  }
}

.rank-badge {
  display: inline-block;
  padding: 2px 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 11px;
  font-weight: 700;
  border-radius: 3px;
  
  &.large {
    font-size: 14px;
    padding: 4px 12px;
  }
}

.match-score {
  font-size: 12px;
  font-weight: 600;
  color: #667eea;
  white-space: nowrap;
  
  &.large {
    font-size: 16px;
  }
}

.detail-content {
  .detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 2px solid #e2e8f0;
    
    h3 {
      margin: 0;
      font-size: 20px;
      color: #1e293b;
      display: flex;
      align-items: center;
      gap: 12px;
    }
  }
  
  .detail-body {
    max-height: 60vh;
    overflow-y: auto;
  }
  
  .detail-text {
    font-size: 13px;
    line-height: 1.8;
    color: #475569;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
    margin: 0;
    padding: 16px;
    background: #f8fafc;
    border-radius: 6px;
  }
}

// 下拉动画
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  max-height: 200px;
  overflow: hidden;
}

.slide-down-enter,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
